import { ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Typography } from "@digital-services/ui";

import ErrorComponent from "@components/ErrorComponent";
import LoadingComponent from "@components/LoadingComponent";

import { ApplicationJsonItemResponse } from "@applications/models/ApplicationJsonModel";
import { useGetAssociatedApplications } from "@applications/services/ApplicationService";

import { ApplicationAssociation } from "./ApplicationAssociation";

interface Props {
  isDisabled?: boolean;
  onAppRoleChange: (
    applicationId: string,
    applicationName: string,
    applicationRoles: string[],
    isValid: boolean,
    isChecked: boolean,
    errorMessage: string | undefined
  ) => void;
}

export function ApplicationAssociations(props: Readonly<Props>) {
  const { isDisabled = false, onAppRoleChange } = props;

  const { t } = useTranslation(undefined, { keyPrefix: "user.applications" });

  const { applications, isLoading, error } = useGetAssociatedApplications();

  const renderChildren = useCallback(
    (children: ReactNode): ReactNode => {
      return (
        <>
          <Typography variant="smallTitle">{t("section_title")}</Typography>
          <div className="flex flex-col gap-5">{children}</div>
        </>
      );
    },
    [t]
  );

  if (isLoading)
    return renderChildren(<LoadingComponent message={t("load.message")} />);
  if (error)
    return renderChildren(<ErrorComponent message={t("load.error.message")} />);

  if (applications!.length === 0)
    return renderChildren(<>{t("load.none.message")}</>);

  const filteredApps: ApplicationJsonItemResponse[] = [];
  for (const item of applications!) {
    if (item.error) {
      continue;
    }

    filteredApps.push(item);
  }

  return renderChildren(
    <>
      {filteredApps?.map((appResponseItem: ApplicationJsonItemResponse) => {
        const application = appResponseItem?.data;

        return (
          <ApplicationAssociation
            key={"appAssoc_" + application.id}
            applicationId={application.id}
            applicationName={application.name}
            appEnv={application.appEnv}
            isDisabled={isDisabled}
            onAppRoleChange={onAppRoleChange}
          />
        );
      })}
    </>
  );
}
